// src/components/Spinner.jsx (or wherever you store your components)

import React from 'react';

/**
 * A spinning loader component with optional text and size control.
 * Uses Tailwind CSS for styling and animation.
 *
 * @param {object} props - The component props.
 * @param {string} [props.text] - The text to display next to the spinner.
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - The size of the spinner ('sm', 'md', 'lg').
 * @param {string} [props.className=''] - Additional CSS classes to apply to the main container.
 */
const Spinner = ({ text, size = 'md', className = '' }) => {
  // Helper to determine spinner dimensions and border width based on size prop
  const getSpinnerStyles = (spinnerSize) => {
    switch (spinnerSize) {
      case 'sm':
        return 'w-4 h-4 border-2'; // Small spinner, thinner border
      case 'md':
        return 'w-6 h-6 border-4'; // Medium spinner (default), standard border
      case 'lg':
        return 'w-8 h-8 border-6'; // Large spinner, thicker border
      default:
        return 'w-6 h-6 border-4'; // Fallback to medium
    }
  };

  const spinnerClasses = getSpinnerStyles(size);

  return (
    <div
      className={`flex items-center ${text ? 'gap-x-2' : ''} ${className}`}
      role="status" // Indicate to screen readers that this is a live region showing status
      aria-live="polite" // Announce changes politely without interrupting
      aria-label={text ? text : "Loading..."} // Provide a descriptive label for accessibility
    >
      <div
        className={`rounded-full animate-spin border-gray-300 border-t-primary ${spinnerClasses}`}
      />
      {text && (
        // Display text if provided, apply some default styling
        <span className="text-gray-700 dark:text-gray-300 font-sans">
          {text}
        </span>
      )}
    </div>
  );
};

export default Spinner;